import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { formatBRL } from "@/lib/scrollTo";
import { resolveImg } from "@/lib/img";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AdminImoveis = () => {
  const [items, setItems] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/properties").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await api.delete(`/admin/properties/${toDelete.id}`);
      toast.success("Imóvel excluído.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-imoveis-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-brand">Imóveis</p>
          <h1 className="mt-2 font-serif text-2xl text-[#F4F4F6] sm:text-3xl">
            {items ? `${items.length} ${items.length === 1 ? "imóvel cadastrado" : "imóveis cadastrados"}` : "Carregando..."}
          </h1>
        </div>
        <Link
          to="/admin/imoveis/novo"
          data-testid="admin-new-property-button"
          className="inline-flex items-center gap-2 bg-brand px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-brand-light"
        >
          <Plus size={14} /> Novo imóvel
        </Link>
      </div>

      <div className="mt-8 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-coal font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
            <tr>
              <th className="px-4 py-3">Imóvel</th>
              <th className="px-4 py-3">Negócio</th>
              <th className="px-4 py-3">Cidade</th>
              <th className="px-4 py-3">Preço</th>
              <th className="px-4 py-3">Destaque</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {items?.map((p) => (
              <tr key={p.id} data-testid={`admin-property-row-${p.id}`} className="transition-colors hover:bg-coal/60">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={resolveImg(p.imagens?.[0])} alt="" className="h-12 w-16 shrink-0 object-cover" />
                    <div>
                      <p className="text-stone-100">{p.titulo}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">{p.tipo}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-300">{p.negocio}</td>
                <td className="px-4 py-3 text-stone-300">{p.cidade}</td>
                <td className="px-4 py-3 text-stone-100">{formatBRL(p.preco, p.negocio)}</td>
                <td className="px-4 py-3">{p.destaque && <Star size={14} className="fill-sun text-sun" />}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Link to={`/admin/imoveis/${p.id}/editar`} data-testid={`admin-edit-${p.id}`} aria-label="Editar" className="flex h-9 w-9 items-center justify-center border border-white/10 text-stone-300 transition-colors hover:border-brand hover:text-brand">
                      <Pencil size={14} />
                    </Link>
                    <button onClick={() => setToDelete(p)} data-testid={`admin-delete-${p.id}`} aria-label="Excluir" className="flex h-9 w-9 items-center justify-center border border-white/10 text-stone-300 transition-colors hover:border-brand hover:text-brand">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items?.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-500">Nenhum imóvel cadastrado ainda.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none border-white/10 bg-coal text-stone-100" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Excluir imóvel?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              "{toDelete?.titulo}" será removido do site. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/10 bg-transparent text-stone-300 hover:bg-white/5" data-testid="admin-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-brand text-white hover:bg-brand-light" data-testid="admin-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminImoveis;
